import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Badge,
    Button,
    HStack,
    VStack,
    Text,
    Table,
    Spinner,
    IconButton,
} from "@chakra-ui/react";
import { HiArrowDownTray, HiOutlineClipboard } from "react-icons/hi2";
import InteractionService from "@/services/agency/InteractionService";
import { ChannelBadge } from "@/components/ui/detail/primitives";

const channelOf = (interaction) =>
    interaction?.whatsAppId ? "WhatsApp" : interaction?.contactEmail ? "Email" : "Chat";

const tableHeaderStyle = {
    color: "text.grey",
    fontWeight: 600,
    fontSize: "xs",
    textTransform: "uppercase",
    letterSpacing: "wider",
    py: 3,
    px: 5,
    border: "none",
};

const InfoField = ({ label, children }) => (
    <VStack align="flex-start" gap={1}>
        <Text color="text.grey" fontSize="xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider">
            {label}
        </Text>
        {children}
    </VStack>
);

const formatBytes = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const parseJson = (value) => {
    if (!value) return null;
    try {
        return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
        return null;
    }
};

const JsonSection = ({ title, data, downloadFilename }) => {
    const parsed = parseJson(data);
    const json = parsed ? JSON.stringify(parsed, null, 2) : null;

    const handleCopy = () => navigator.clipboard.writeText(json);

    const handleDownload = () => {
        const blob = new Blob([json], { type: "application/json" });
        triggerBlobDownload(blob, downloadFilename);
    };

    return (
        <VStack w="full" align="flex-start" gap={3}>
            <HStack w="full" justify="space-between">
                <Text fontWeight={700} fontSize="lg" color="white">{title}</Text>
                {json && (
                    <HStack gap={2}>
                        <IconButton
                            variant="ghost"
                            size="sm"
                            aria-label="Kopiuj JSON"
                            onClick={handleCopy}
                            color="text.grey"
                            _hover={{ color: "white" }}
                        >
                            <HiOutlineClipboard />
                        </IconButton>
                        <IconButton
                            variant="ghost"
                            size="sm"
                            aria-label="Pobierz JSON"
                            onClick={handleDownload}
                            color="text.grey"
                            _hover={{ color: "white" }}
                        >
                            <HiArrowDownTray />
                        </IconButton>
                    </HStack>
                )}
            </HStack>
            <Box
                w="full"
                bg="bg.card"
                border="1px solid"
                borderColor="border.primary"
                borderRadius={12}
                p={5}
                overflow="hidden"
            >
                {json ? (
                    <Box
                        as="pre"
                        bg="bg.secondary"
                        borderRadius={8}
                        overflow="auto"
                        p={4}
                        whiteSpace="break-spaces"
                        maxH="400px"
                        fontSize="sm"
                        fontFamily="mono"
                        color="white"
                    >
                        {json}
                    </Box>
                ) : (
                    <Text color="text.grey" fontSize="sm">Brak danych</Text>
                )}
            </Box>
        </VStack>
    );
};

const InteractionDetailPage = () => {
    const { interactionId } = useParams();
    const [interaction, setInteraction] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        InteractionService.getById(interactionId)
            .then(setInteraction)
            .catch(() => setNotFound(true))
            .finally(() => setIsLoading(false));
    }, [interactionId]);

    const handleDownloadFile = async (file) => {
        const response = await InteractionService.downloadAttachment(interactionId, file.name);
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        triggerBlobDownload(blob, file.name.split("/").pop());
    };

    const handleDownloadAll = async () => {
        const response = await InteractionService.downloadAllAttachments(interactionId);
        const blob = new Blob([response.data], { type: 'application/zip' });
        triggerBlobDownload(blob, `interaction_${interactionId}.zip`);
    };

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="brand.400" size="lg" />
            </VStack>
        );
    }

    if (notFound || !interaction) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="text.grey">Nie znaleziono interakcji.</Text>
            </VStack>
        );
    }

    const isWhatsApp = interaction.whatsAppId != null;
    const channel = channelOf(interaction);
    const participant = interaction.contactEmail ?? interaction.whatsAppId ?? null;
    const displayName = interaction.queue ?? interaction.sessionId;

    return (
        <VStack w="full" py={6} gap={6} align="flex-start">
            {/* Header card */}
            <Box
                w="full"
                bg="bg.card"
                border="1px solid"
                borderColor="border.primary"
                borderRadius={12}
                p={5}
            >
                <HStack justify="space-between" align="flex-start" mb={4}>
                    <VStack align="flex-start" gap={1}>
                        <Text fontWeight={700} fontSize="lg" color="white">{displayName}</Text>
                        <HStack gap={2}>
                            <Text color="text.grey" fontSize="xs" fontFamily="mono">{interaction.id}</Text>
                            <IconButton
                                variant="ghost"
                                size="xs"
                                aria-label="Kopiuj ID"
                                color="text.grey"
                                _hover={{ color: "white" }}
                                onClick={() => navigator.clipboard.writeText(interaction.id)}
                            >
                                <HiOutlineClipboard />
                            </IconButton>
                        </HStack>
                    </VStack>
                    <ChannelBadge channel={channel} />
                </HStack>
                <HStack gap={8} align="flex-start" flexWrap="wrap">
                    {participant && (
                        <InfoField label="Z kim">
                            <Text color="white" fontSize="sm" fontFamily="mono">{participant}</Text>
                        </InfoField>
                    )}
                    <InfoField label="Utworzono">
                        <Text color="white" fontSize="sm">
                            {new Date(interaction.createdAt).toLocaleString("pl-PL")}
                        </Text>
                    </InfoField>
                    <InfoField label="Zaktualizowano">
                        <Text color="white" fontSize="sm">
                            {interaction.updatedAt
                                ? new Date(interaction.updatedAt).toLocaleString("pl-PL")
                                : "—"}
                        </Text>
                    </InfoField>
                    <InfoField label="Scenariusz">
                        <Text color="white" fontSize="sm" fontFamily="mono">
                            {interaction.scenario?.name ?? "—"}
                        </Text>
                    </InfoField>
                    <InfoField label="Flow">
                        <Text color="white" fontSize="sm" fontFamily="mono">
                            {interaction.flowId ?? "—"}
                        </Text>
                    </InfoField>
                    {isWhatsApp && (
                        <InfoField label="WhatsApp ID">
                            <Text color="white" fontSize="sm" fontFamily="mono">
                                {interaction.whatsAppId}
                            </Text>
                        </InfoField>
                    )}
                </HStack>
            </Box>

            <JsonSection
                title="Zebrane sloty"
                data={interaction.collectedSlots}
                downloadFilename={`slots_${interactionId}.json`}
            />

            <JsonSection
                title="Wypełnione encje"
                data={interaction.fulfilledEntities}
                downloadFilename={`entities_${interactionId}.json`}
            />

            {/* Attachments */}
            <VStack w="full" align="flex-start" gap={3}>
                <HStack w="full" justify="space-between">
                    <Text fontWeight={700} fontSize="lg" color="white">Załączniki</Text>
                    {interaction.files?.length > 0 && (
                        <Button variant="outlined" size="sm" onClick={handleDownloadAll}>
                            <HiArrowDownTray /> Pobierz wszystko (ZIP)
                        </Button>
                    )}
                </HStack>
                <Box
                    w="full"
                    border="1px solid"
                    borderColor="border.primary"
                    borderRadius={12}
                    overflowX="auto"
                >
                    {!interaction.files?.length ? (
                        <Box bg="bg.card" py={10} textAlign="center">
                            <Text color="text.grey" fontSize="sm">Brak załączników</Text>
                        </Box>
                    ) : (
                        <Table.Root>
                            <Table.Header>
                                <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                    {["Nazwa", "Typ", "Data", "Rozmiar", "Akcje"].map((col, i) => (
                                        <Table.ColumnHeader
                                            key={col}
                                            {...tableHeaderStyle}
                                            textAlign={i === 4 ? "right" : "left"}
                                        >
                                            {col}
                                        </Table.ColumnHeader>
                                    ))}
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {interaction.files.map((file) => (
                                    <Table.Row
                                        key={file.name}
                                        bg="bg.secondary"
                                        borderBottom="1px solid"
                                        borderColor="border.primary"
                                        _hover={{ bg: "bg.highlight" }}
                                        transition="background 0.15s"
                                    >
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="white" fontSize="sm" fontFamily="mono">
                                                {file.name.split("/").pop()}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="text.grey" fontSize="xs">{file.contentType}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="white" fontSize="sm">
                                                {file.createdAt
                                                    ? new Date(file.createdAt).toLocaleDateString("pl-PL")
                                                    : "—"}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="white" fontSize="sm">{formatBytes(file.size)}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" textAlign="right">
                                            <IconButton
                                                variant="outlined"
                                                size="sm"
                                                aria-label="Pobierz plik"
                                                onClick={() => handleDownloadFile(file)}
                                            >
                                                <HiArrowDownTray />
                                            </IconButton>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    )}
                </Box>
            </VStack>
        </VStack>
    );
};

export default InteractionDetailPage;
